import { customerDirectionIndex, type DirectionSelection } from './development-model';
import type { DevelopmentContent } from '@sqi/shared-schemas';
import type { evaluateDevelopmentFormula, indicativeDevelopmentCost } from '@sqi/formula-core';
import { fileBlob } from '../../lib/oem-client';
import { loadOemPdfRenderer } from './pdf-renderer';
import type { PropsWithChildren, ReactNode } from 'react';
import type { TextProps } from '@react-pdf/renderer';
const text = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'ยังไม่ระบุ';
  // Break very long unspaced URLs/identifiers without changing the underlying saved JSON.
  return (
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : 'ยังไม่ระบุ'
  ).replace(/([^\s]{35})(?=[^\s])/g, '$1\u200b');
};
const originLabels: Record<string, string> = {
  new_experimental: 'สูตรทดลองใหม่ · ยังไม่อนุมัติ',
  modified_base: 'ปรับจากฐานสูตร · ต้องทบทวนใหม่',
  approved_base: 'ฐานสูตรที่ผ่านการตรวจตามหลักฐาน',
};
const qualityLabels: Record<string, string> = {
  unreviewed: 'ยังไม่ตรวจภาพ',
  readable: 'อ่านได้',
  blurred: 'ภาพเบลอ',
  incomplete: 'ข้อมูลไม่ครบ',
};
const uncertaintyLabels: Record<string, string> = {
  unverified: 'ยังไม่ยืนยัน',
  supported: 'มีหลักฐานประกอบ',
  conflicting: 'ข้อมูลขัดแย้ง',
  inaccessible: 'เปิดแหล่งไม่ได้',
};
export async function renderDevelopmentPdf(snapshot: {
  id: string;
  version: number;
  hash: string;
  audience: string;
  content: unknown;
  directionSelection?: DirectionSelection;
  calculations?: unknown;
  disclaimer: string;
  status: string;
}) {
  const { Document, Page, Text: BaseText, View, Image, pdf } = await loadOemPdfRenderer();
  // Decompose SARA AM only for stock PDF shaping, which can otherwise omit
  // terminal Thai glyphs. Saved JSON stays composed; PDF extraction may differ.
  const pdfText = (value: ReactNode): ReactNode => {
    if (typeof value === 'string') return value.replace(/\u0e33/g, '\u0e4d\u0e32');
    if (Array.isArray(value)) return value.map((child: ReactNode) => pdfText(child));
    return value;
  };
  const Text = ({ children, render, ...props }: PropsWithChildren<TextProps>) => {
    const dynamic: Pick<TextProps, 'render'> = render
      ? { render: (context) => pdfText(render(context)) }
      : {};
    return (
      <BaseText {...props} {...dynamic}>
        {pdfText(children)}
      </BaseText>
    );
  };
  const content = snapshot.content as DevelopmentContent;
  const intake = content.intake;
  const selectedDirection = customerDirectionIndex(
    snapshot.directionSelection,
    snapshot,
    content.directions.length,
  );
  const internal = snapshot.audience === 'internal';
  const calculations = (snapshot.calculations ?? []) as {
    formula: ReturnType<typeof evaluateDevelopmentFormula>;
    cost: ReturnType<typeof indicativeDevelopmentCost>;
  }[];
  const images = await Promise.all(
    (content.photos ?? []).map(async (photo) => {
      try {
        const blob = await fileBlob(photo.derivativeId);
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            typeof reader.result === 'string'
              ? resolve(reader.result)
              : reject(Error('Invalid photo data'));
          reader.onerror = () => reject(Error('Photo unavailable'));
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    }),
  );
  const paragraph = (label: string, value: unknown) => (
    <View key={label} style={{ marginBottom: 9 }}>
      <Text style={{ color: '#426358', fontWeight: 700, marginBottom: 3 }}>{label}</Text>
      <Text>{text(value)}</Text>
    </View>
  );
  const heading = (title: string) => (
    <Text
      style={{ fontSize: 12, fontWeight: 700, marginTop: 16, marginBottom: 12, color: '#244e3e' }}
    >
      {title}
    </Text>
  );
  const table = (headers: string[], rows: unknown[][], widths?: number[]) => (
    <View style={{ marginBottom: 12 }}>
      <View wrap={false} style={{ flexDirection: 'row', backgroundColor: '#e9f0eb', padding: 7 }}>
        {headers.map((h, i) => (
          <Text
            key={h}
            style={{
              width: `${widths?.[i] ?? 100 / headers.length}%`,
              fontWeight: 700,
              paddingRight: 5,
            }}
          >
            {h}
          </Text>
        ))}
      </View>
      {rows.map((row, i) => (
        <View
          key={i}
          style={{
            flexDirection: 'row',
            padding: 7,
            borderBottomWidth: 0.5,
            borderBottomColor: '#d2ded7',
          }}
        >
          {row.map((cell, j) => (
            <Text
              key={j}
              style={{ width: `${widths?.[j] ?? 100 / headers.length}%`, paddingRight: 5 }}
            >
              {text(cell)}
            </Text>
          ))}
        </View>
      ))}
    </View>
  );
  const sample = content.sample;
  return pdf(
    <Document title={`SQI development ${snapshot.id} v${snapshot.version}`}>
      <Page
        size="A4"
        style={{
          paddingTop: 36,
          paddingBottom: 60,
          paddingHorizontal: 36,
          fontFamily: 'Noto Sans Thai',
          fontSize: 9,
          lineHeight: 1.7,
          color: '#203b31',
        }}
      >
        <View wrap={false} style={{ marginBottom: 14 }}>
          <Text style={{ fontSize: 18, lineHeight: 1.9, marginBottom: 16, fontWeight: 700 }}>
            SQI · {internal ? 'ใบงาน R&D ภายใน' : 'บรีฟพัฒนาผลิตภัณฑ์'}
          </Text>
          <Text style={{ marginBottom: 8 }}>EXPERIMENTAL · งานพัฒนาทดลอง ไม่ใช่การอนุญาตผลิต</Text>
          <Text style={{ marginBottom: 8 }}>{snapshot.disclaimer}</Text>
          <Text style={{ fontSize: 8 }}>
            ฉบับที่บันทึก v{snapshot.version} · รหัส {text(snapshot.id)}
          </Text>
          <Text style={{ fontSize: 7, marginTop: 4 }}>SHA-256: {text(snapshot.hash)}</Text>
        </View>
        {paragraph(
          'การเลือกแนวทางของลูกค้าบนฉบับนี้',
          selectedDirection === null
            ? snapshot.directionSelection?.state === 'conflict'
              ? 'ข้อมูลการเลือกขัดแย้ง ต้องใช้ฉบับใหม่ ประวัติเดิมยังอยู่'
              : 'ยังไม่มีการเลือกของลูกค้า ข้อเสนอในร่างไม่ใช่การยืนยัน'
            : `แนวทาง ${selectedDirection + 1} · การตัดสินใจ ${snapshot.directionSelection?.decisionId} · วันที่ ${snapshot.directionSelection?.createdAt}`,
        )}
        {heading('1. ความต้องการพัฒนาผลิตภัณฑ์')}
        {table(
          ['รายการ', 'รายละเอียด'],
          [
            ['โครงการ', intake.label],
            ['ลิงก์สินค้าอ้างอิงสาธารณะ', intake.referenceUrl],
            [
              'ประเภท',
              (
                {
                  moisturizer: 'มอยส์เจอไรเซอร์',
                  body_wash: 'เจลอาบน้ำ',
                  cosmetic_serum: 'เซรั่มเครื่องสำอาง',
                  unknown: 'ยังไม่ทราบ',
                  specialist: 'ผู้เชี่ยวชาญต้องตรวจ',
                } as Record<string, string>
              )[intake.category],
            ],
            ['ตลาดปลายทาง', intake.market],
            ['เนื้อสัมผัส', intake.texture],
            ['กลิ่น', intake.fragrance],
            ['ประโยชน์ที่คาดหวัง (ยังไม่ใช่ claim ที่พิสูจน์)', intake.benefits],
            ['ส่วนผสมที่หลีกเลี่ยง', intake.avoidedIngredients],
            [
              'ขนาด / จำนวน',
              `${text(intake.fillSize)} ${intake.fillUnit} / ${text(intake.quantity)} ชิ้น`,
            ],
            [
              'เป้าต้นทุนโรงงานต่อหน่วย (ไม่บังคับ)',
              intake.factoryTargetPrice === null
                ? 'ยังไม่ระบุ'
                : `${intake.factoryTargetPrice} THB`,
            ],
            [
              'เป้าราคาขายต่อหน่วย (ไม่บังคับ)',
              intake.retailTargetPrice === null ? 'ยังไม่ระบุ' : `${intake.retailTargetPrice} THB`,
            ],
            [
              'การยืนยัน',
              `ตัวตนอ้างอิง: ${content.identityConfirmed ? 'ยืนยันบนข้อมูลฉบับนี้' : 'รอยืนยัน'} / บรีฟ: ${content.briefConfirmed ? 'ยืนยันบนข้อมูลฉบับนี้' : 'รอยืนยัน'}`,
            ],
          ],
          [33, 67],
        )}
        {heading('2. สินค้าอ้างอิงและแหล่งหลักฐาน')}
        {(content.candidates ?? []).map((candidate, i) => (
          <View key={i}>
            {paragraph(
              `สินค้าอ้างอิง ${i + 1}${content.selectedCandidate === i ? ' · ตัวเลือกที่ยืนยัน' : ''}`,
              `${candidate.brand} / ${candidate.product} / ${candidate.variant}`,
            )}
            {paragraph(
              'ตลาด / ขนาดของสินค้าอ้างอิง',
              `${candidate.referenceMarket} / ${candidate.referenceSize}`,
            )}
            {paragraph('ตัวตนที่บันทึก', candidate.identity)}
            {table(
              ['ประเด็น / ข้อมูล', 'แหล่ง / วันที่', 'สถานะหลักฐาน'],
              candidate.evidence.map((e) => [
                `${e.field}: ${e.value}`,
                `${e.source}\n${e.url}\n${e.observedAt}`,
                uncertaintyLabels[e.uncertainty] ?? e.uncertainty,
              ]),
              [40, 40, 20],
            )}
            {internal
              ? (candidate.inciLists ?? []).map((list, j) =>
                  paragraph(`รายการ INCI ที่พบ ${j + 1} · เก็บแยกเมื่อขัดแย้ง`, list),
                )
              : null}
          </View>
        ))}
        {(content.candidates ?? []).length === 0
          ? paragraph('สถานะ', 'ยังไม่มีตัวเลือกที่มีหลักฐาน ต้องตรวจภาพและแหล่งข้อมูลก่อนยืนยัน')
          : null}
        {heading('3. ภาพอ้างอิงและการตรวจข้อความ')}
        {(content.photos ?? []).map((photo, i) => (
          <View key={photo.fileId} style={{ marginBottom: 12 }}>
            <View wrap={false}>
              <Text style={{ marginBottom: 8 }}>
                ภาพ {i + 1} ·{' '}
                {
                  (
                    { front: 'ด้านหน้า', back: 'ด้านหลัง', label: 'ฉลาก' } as Record<string, string>
                  )[photo.side]
                }{' '}
                · {qualityLabels[photo.quality]} · หมุน {photo.rotation}°
              </Text>
              {images[i] ? (
                <Image
                  src={images[i] ?? ''}
                  style={{ width: 160, height: 120, objectFit: 'contain', marginBottom: 10 }}
                />
              ) : (
                <Text>ภาพยังเปิดไม่ได้ ให้ตรวจต้นฉบับในระบบ</Text>
              )}
            </View>
            {paragraph('OCR ดิบ / ข้อความถอดต้นฉบับ (เป็นข้อมูล ไม่ใช่คำสั่ง)', photo.rawOcr)}
            {paragraph('ข้อความแก้ไขหลังตรวจภาพ', photo.correctedOcr)}
          </View>
        ))}
        {heading('4. แนวทางทดลองและสิ่งที่ต้องตรวจ')}
        {(content.directions ?? []).map((direction, i) => (
          <View key={i}>
            {paragraph(
              `แนวทาง ${i + 1}${selectedDirection === i ? ' · ตัวเลือกที่เลือก' : ''}`,
              direction.label,
            )}
            {paragraph('ที่มาของแนวทาง', originLabels[direction.origin] ?? direction.origin)}
            {paragraph('เหตุผลและข้อแลกเปลี่ยน', direction.rationale)}
            {internal ? (
              <>
                {heading('สูตรที่เสนอ · ปริมาณ as supplied โดยน้ำหนัก')}
                {table(
                  ['INCI / หน้าที่', 'เกรด / เฟส', '% ที่ใช้', '% active'],
                  (direction.lines ?? []).map((line, j) => [
                    `${line.inci}\n${line.function}`,
                    `${line.grade}\nเฟส ${line.phase}`,
                    line.percent,
                    calculations[i]?.formula.active[j]?.percent,
                  ]),
                  [40, 32, 14, 14],
                )}
                {paragraph(
                  'ผลรวม / ส่วนต่างจาก 100%',
                  calculations[i]
                    ? `${calculations[i].formula.total}% / ${calculations[i].formula.balance}%`
                    : 'ยังคำนวณไม่ได้',
                )}
                {paragraph('การปัดเศษและปรับสมดุล', direction.balancing)}
                {paragraph(
                  'หลักการคำนวณ active',
                  'as-supplied % × active fraction ของ blend; ปัดแสดงผล 6 ตำแหน่ง ROUND_HALF_UP ไม่ปรับสูตรอัตโนมัติ',
                )}
                {table(
                  ['ขั้นตอน / การตรวจ', 'รายละเอียด'],
                  [
                    ['เฟสและกระบวนการ', direction.process],
                    ['เครื่องมือ', direction.equipment],
                    ['เป้าหมายและเกณฑ์', direction.targets],
                    ['ระบบกันเสีย', direction.preservation],
                    ['แผนทดสอบ', direction.tests],
                    ['ความเข้ากันได้', direction.compatibility],
                    ['การทดแทน', direction.substitutions],
                    ['การทบทวนใหม่', direction.revalidation],
                    ['สมมติฐานและหลักฐาน', direction.assumptions],
                    ['ความเสี่ยง', direction.risks],
                  ],
                  [30, 70],
                )}
                {heading('ต้นทุนเบื้องต้น · แยกยอดที่ทราบจากรายการที่ไม่ทราบ')}
                {table(
                  ['วัตถุดิบ', 'THB/kg / สถานะ', 'แหล่ง / วันที่'],
                  (direction.lines ?? []).map((l) => [
                    l.inci,
                    `${text(l.supplierPricePerKg)} / ${l.priceClass}`,
                    `${l.priceSource}\n${l.priceDate}`,
                  ]),
                  [30, 25, 45],
                )}
                {table(
                  ['ค่าใช้จ่าย', 'จำนวน / ฐานคำนวณ', 'ที่มา / วันที่'],
                  (content.costs ?? []).map((charge) => [
                    charge.label,
                    `${text(charge.amountTHB)} THB / ${charge.basis === 'per_unit' ? 'ต่อชิ้น' : 'ต่อ batch'}`,
                    `${charge.source}\n${charge.date} / ${charge.provenance}`,
                  ]),
                  [30, 25, 45],
                )}
                {calculations[i]
                  ? table(
                      ['ผลคำนวณ / สมมติฐาน', 'รายละเอียด'],
                      [
                        ['มวลที่ต้องใช้รวม yield', `${text(calculations[i].cost.massKg)} kg`],
                        [
                          'ความหนาแน่น / usable yield',
                          `${text(intake.densityGml)} g/mL / ${text(intake.yieldFraction)}`,
                        ],
                        [
                          'ยอดวัตถุดิบที่ทราบ',
                          `${calculations[i].cost.knownRawMaterialSubtotalTHB} THB`,
                        ],
                        ['ค่าใช้จ่ายอื่นที่ทราบ', `${calculations[i].cost.knownOtherCostsTHB} THB`],
                        [
                          'ยอดรวมที่ทราบ / ต่อชิ้น',
                          `${calculations[i].cost.knownSubtotalTHB} THB / ${text(calculations[i].cost.knownPerUnitTHB)} THB`,
                        ],
                        [
                          'ราคาที่ยังไม่ทราบ',
                          calculations[i].cost.missingPrices.join(', ') || 'ไม่มี',
                        ],
                        [
                          'ค่าใช้จ่ายที่ยังไม่ทราบ',
                          calculations[i].cost.missingCharges.join(', ') || 'ไม่มี',
                        ],
                        [
                          'สถานะ / ข้อควรตรวจ',
                          `${calculations[i].cost.status} / ${calculations[i].cost.issues.join(', ') || 'ไม่มีข้อผิดพลาดทางตัวเลข'}`,
                        ],
                        [
                          'ข้อจำกัด',
                          'ไม่รวมภาษีและกำไร; ยอดที่ทราบอาจเป็นศูนย์เพราะราคายังไม่ทราบ ไม่ใช่ต้นทุนศูนย์หรือใบเสนอราคาสุดท้าย',
                        ],
                      ],
                      [35, 65],
                    )
                  : null}
              </>
            ) : (
              <Text style={{ marginBottom: 12 }}>
                แนวทางนี้ยังเป็นข้อเสนอทดลอง ประสิทธิภาพ ความเข้ากันได้
                การกันเสียและความปลอดภัยต้องตรวจจากตัวอย่างและการทดสอบก่อนใช้จริง
              </Text>
            )}
          </View>
        ))}
        {(content.directions ?? []).length === 0
          ? paragraph('สถานะ', 'ยังไม่มีแนวทางทดลอง ต้องยืนยันตัวตนอ้างอิง บรีฟ และประเภทก่อน')
          : null}
        {heading('5. ตัวอย่าง ความเห็น และเรื่องที่ยังรอ')}
        {paragraph(
          'ตัวอย่างที่ผูกกับฉบับนี้',
          sample?.sampleId
            ? `รหัส ${sample.sampleId} / hash ${sample.sampleHash}`
            : 'ยังไม่มีตัวอย่างที่ผูกกับสูตร กระบวนการและคำขอที่ตรวจแล้ว',
        )}
        {paragraph('ความคิดเห็นลูกค้า', sample?.feedback)}
        {paragraph('คำขอแก้ไข', sample?.changeRequest)}
        {paragraph(
          'เงื่อนไขก่อนความพร้อม',
          'ต้องตรวจ R&D โดยผู้ตรวจคนละคน ตรวจ QA จากหลักฐานที่ใช้ได้ เตรียมตัวอย่างตามฉบับตรงกัน รับ feedback และยืนยันตัวอย่างฉบับนั้น การแก้ไขทำฉบับใหม่และทบทวนส่วนที่ได้รับผลกระทบ ประวัติการตรวจเดิมยังอยู่ในระบบ',
        )}
        {paragraph(
          'ขอบเขตเอกสาร',
          'เอกสารจากข้อมูลที่บันทึกฉบับนี้เท่านั้น ไม่ใช่สูตรลับของสินค้าอ้างอิง ไม่ใช่การอนุมัติด้านกฎหมาย การยืนยันการผลิต หรือใบเสนอราคาสุดท้าย',
        )}
        <View
          fixed
          style={{
            position: 'absolute',
            bottom: 20,
            left: 36,
            right: 36,
            borderTopWidth: 0.5,
            borderTopColor: '#cbd8d0',
            paddingTop: 6,
          }}
          // Recreate both lines together on each pagination pass so inherited,
          // resolved line heights are not multiplied again by the renderer.
          render={() => (
            <>
              <Text style={{ fontSize: 7, lineHeight: '10pt' }}>
                EXPERIMENTAL · ไม่ใช่การอนุญาตผลิต · v{snapshot.version} ·{' '}
                {snapshot.hash.slice(0, 16)}
              </Text>
              <Text
                style={{ fontSize: 7, lineHeight: '10pt' }}
                render={({ pageNumber, totalPages }) =>
                  `หน้า ${pageNumber} / ${totalPages} · ${snapshot.id}`
                }
              />
            </>
          )}
        />
      </Page>
    </Document>,
  ).toBlob();
}
export function downloadDevelopment(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
